import { type FC, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { Table, TableHead, TableHeadCell, TableBody, TableRow, TableCell, Spinner, TextInput, Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, Button } from "flowbite-react";
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { HiSearch, HiHome, HiOutlineExclamationCircle } from "react-icons/hi";
import { MultiSelect } from "../../components/MultiSelect";
import DateRangePicker from "../../components/DateRangePicker";
import { getActiveWorkerFilterOptions } from "../../services/workers";
import TeamSelector, { type TeamSelection } from "../../components/dashboard/TeamSelector";
import { useAdminAccess } from "../../hooks/useAdminAccess";
import { getCurrentWorkerProfile, type CurrentWorkerProfile } from "../../services/current-worker";
import { getWorkersByTeam } from "../../services/teams";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  created_at: string;
  address: {
    street: string;
    house_number: string;
    zip_code: string;
    city: string;
  };
  contracts: {
    id: number;
    secure_token: string;
    contract_number: string;
    contract_type: {
      type: string;
      company: string;
    };
    worker: {
      full_name: string;
    };
  }[];
}

const AllCustomersPage: FC = function () {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [workerFilterOptions, setWorkerFilterOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { roleId } = useAdminAccess();

  // Team filtering state
  const [currentWorker, setCurrentWorker] = useState<CurrentWorkerProfile | null>(null);
  const [teamSelection, setTeamSelection] = useState<TeamSelection>({ type: 'all' });
  const [teamWorkerIds, setTeamWorkerIds] = useState<number[] | null>(null);
  const [teamSelectionReady, setTeamSelectionReady] = useState(false);

  const isAdminOrDirection = roleId === 1 || roleId === 2;
  const isTeamLead = roleId === 3;

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  // Add states for applied filters
  const [appliedFilters, setAppliedFilters] = useState<{
    searchTerm: string;
    types: string[];
    companies: string[];
    workers: string[];
    dateRange: {
      startDate: Date | null;
      endDate: Date | null;
    };
  }>({
    searchTerm: "",
    types: [],
    companies: [],
    workers: [],
    dateRange: {
      startDate: null,
      endDate: null,
    },
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete customer modal state
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);

  // Extract unique values for filters
  const uniqueValues = useMemo(() => {
    const types = new Set<string>();
    const companies = new Set<string>();

    customers.forEach((customer) => {
      customer.contracts.forEach((contract) => {
        types.add(contract.contract_type.type);
        companies.add(contract.contract_type.company);
      });
    });

    return {
      types: Array.from(types).map((type) => ({
        value: type,
        label: type,
      })),
      companies: Array.from(companies).map((company) => ({
        value: company,
        label: company,
      })),
    };
  }, [customers]);

  // Function to apply filters
  const applyFilters = () => {
    setAppliedFilters({
      searchTerm,
      types: selectedTypes,
      companies: selectedCompanies,
      workers: selectedWorkers,
      dateRange,
    });
    setCurrentPage(1); // Reset to first page when applying filters
  };

  // Function to apply just the search term
  const applySearch = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      searchTerm,
    }));
    setCurrentPage(1); // Reset to first page when searching
  };

  // Function to reset all filters
  const resetFilters = () => {
    // Clear UI filter states
    setSearchTerm("");
    setSelectedTypes([]);
    setSelectedCompanies([]);
    setSelectedWorkers([]);
    setDateRange({
      startDate: null,
      endDate: null,
    });

    // Clear applied filters
    setAppliedFilters({
      searchTerm: "",
      types: [],
      companies: [],
      workers: [],
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });

    setCurrentPage(1); // Reset to first page
  };

  // Replace the current filteredCustomers useMemo with this simpler version
  // that only handles client-side search if needed
  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      // Only apply search term filter client-side if it wasn't already applied server-side
      if (appliedFilters.searchTerm !== searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          searchTerm === "" ||
          `${customer.first_name} ${customer.last_name}`
            .toLowerCase()
            .includes(searchLower) ||
          `${customer.address.street} ${customer.address.house_number} ${customer.address.zip_code} ${customer.address.city}`
            .toLowerCase()
            .includes(searchLower);

        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [customers, searchTerm, appliedFilters.searchTerm]);

  // Fetch current worker profile and initialize team selection
  useEffect(() => {
    if (authLoading || !user) return;
    let mounted = true;

    const initWorker = async () => {
      try {
        const worker = await getCurrentWorkerProfile();
        if (!mounted) return;
        setCurrentWorker(worker);

        // Team leaders default to 'all_my_teams'
        // Admin/direction default to 'all'
        if (worker.role_id === 3) {
          if (mounted) {
            setTeamSelection({ type: 'all_my_teams' });
            // teamSelectionReady will be set by the teamWorkerIds effect
          }
        } else {
          setTeamSelection({ type: 'all' });
          setTeamSelectionReady(true);
        }
      } catch (err) {
        console.error("Error fetching worker profile:", err);
      }
    };

    initWorker();
    return () => { mounted = false; };
  }, [authLoading, user]);

  // Fetch team worker IDs when team selection changes
  useEffect(() => {
    let mounted = true;

    const fetchTeamWorkerIds = async () => {
      if (teamSelection.type === 'all') {
        // Admin/direction viewing all - no team filter
        setTeamWorkerIds(null);
        setTeamSelectionReady(true);
        return;
      }

      if (teamSelection.type === 'individual') {
        // For team leaders waiting for their first team to load
        // Don't set ready yet - wait for TeamSelector to set a team
        if (isTeamLead && !teamSelectionReady) {
          return;
        }
        setTeamWorkerIds(null);
        setTeamSelectionReady(true);
        return;
      }

      if (teamSelection.type === 'all_my_teams') {
        if (!currentWorker) return; // Should not happen

        try {
          // Get all teams for the current worker
          const { data: workerTeamsData, error: workerTeamsError } = await supabase
            .from("worker_teams")
            .select("team_id")
            .eq("worker_id", currentWorker.id)
            .eq("active", true);

          if (workerTeamsError) throw workerTeamsError;

          if (!workerTeamsData || workerTeamsData.length === 0) {
            if (mounted) {
              setTeamWorkerIds([]);
              setTeamSelectionReady(true);
            }
            return;
          }

          const teamIds = workerTeamsData.map(wt => wt.team_id);

          // Get all workers for those teams
          const { data: teamWorkersData, error: teamWorkersError } = await supabase
            .from("worker_teams")
            .select("worker_id")
            .in("team_id", teamIds)
            .eq("active", true);

          if (teamWorkersError) throw teamWorkersError;

          if (mounted) {
            if (!teamWorkersData || teamWorkersData.length === 0) {
              setTeamWorkerIds([]);
            } else {
              // Get unique worker IDs
              const uniqueWorkerIds = [...new Set(teamWorkersData.map(tw => tw.worker_id))];
              setTeamWorkerIds(uniqueWorkerIds);
            }
            setTeamSelectionReady(true);
          }
        } catch (err) {
          console.error("Error fetching all team workers:", err);
          if (mounted) {
            setTeamWorkerIds([]);
            setTeamSelectionReady(true);
          }
        }
        return;
      }

      if (teamSelection.type === 'team') {
        try {
          const workers = await getWorkersByTeam(teamSelection.teamId);
          if (!mounted) return;
          setTeamWorkerIds(workers.map(w => w.id));
          setTeamSelectionReady(true);
        } catch (err) {
          console.error("Error fetching team workers:", err);
          if (mounted) {
            setTeamWorkerIds([]);
            setTeamSelectionReady(true);
          }
        }
      }
    };

    fetchTeamWorkerIds();
    return () => { mounted = false; };
  }, [teamSelection, isTeamLead, teamSelectionReady]);

  // Handle team selection change
  const handleTeamSelectionChange = useCallback((selection: TeamSelection) => {
    setTeamSelection(selection);
    setTeamSelectionReady(false);
    setCurrentPage(1);
  }, []);

  // Auto-select first team for team leaders when TeamSelector hasn't set one yet
  // This is handled by listening for when currentWorker is set and roleId is 3
  // The TeamSelector component will call onSelectionChange with the first team

  useEffect(() => {
    if (authLoading || !user) return;

    let mounted = true;

    const fetchWorkerFilterOptions = async () => {
      try {
        let workers;

        // If a specific team is selected, only show workers from that team
        if (teamSelection.type === 'team' && teamWorkerIds && teamWorkerIds.length > 0) {
          const { data, error } = await supabase
            .from(TABLES.WORKERS)
            .select("id, full_name")
            .in("id", teamWorkerIds)
            .eq("active", true)
            .order("full_name", { ascending: true });

          if (error) throw error;
          workers = data || [];
        } else {
          workers = await getActiveWorkerFilterOptions();
        }

        if (!mounted) return;

        setWorkerFilterOptions(
          workers
            .filter((worker) => worker.full_name?.trim())
            .map((worker) => ({
              value: String(worker.id),
              label: worker.full_name || `Berater #${worker.id}`,
            }))
        );
      } catch (err) {
        console.error("Error fetching worker filter options:", err);
        if (mounted) {
          setError(
            err instanceof Error
              ? err.message
              : "Fehler beim Laden der Berater"
          );
        }
      }
    };

    fetchWorkerFilterOptions();

    return () => {
      mounted = false;
    };
  }, [authLoading, user, teamSelection, teamWorkerIds]);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }

    // Wait until team selection is resolved before fetching
    if (!teamSelectionReady) return;

    const fetchCustomers = async () => {
      try {
        if (!user) return;
        setLoading(true);

        // Variables to store IDs for filters
        let workerIds: number[] = [];
        let typeIds: number[] = [];
        let companyIds: number[] = [];
        let customerIdsForSearch: number[] = [];

        // Start building the query
        let query = supabase.from(TABLES.CONTRACTS).select(
          `
            id,
            secure_token,
            contract_number,
            contract_type:contract_type_id (
              type,
              company
            ),
            worker:worker_id (
              full_name
            ),
            customer:customer_id (
              id,
              first_name,
              last_name,
              email,
              phone_number,
              created_at,
              address:address_id (
                street,
                house_number,
                zip_code,
                city
              )
            )
          `
        );

        // Apply team-based worker scoping
        // For team leaders, always scope to their team's workers
        // For admin/direction with a team selected, scope to that team's workers
        if (teamWorkerIds !== null && teamWorkerIds.length > 0) {
          query = query.in("worker_id", teamWorkerIds);
        } else if (teamWorkerIds !== null && teamWorkerIds.length === 0) {
          // Team has no workers — return empty results
          setCustomers([]);
          setTotalCustomers(0);
          setLoading(false);
          return;
        }

        // Apply server-side filters
        if (appliedFilters.workers.length > 0) {
          workerIds = appliedFilters.workers
            .map((workerId) => Number(workerId))
            .filter((workerId) => Number.isInteger(workerId));

          if (workerIds.length > 0) {
            query = query.in("worker_id", workerIds);
          }
        }

        if (appliedFilters.types.length > 0) {
          // Get contract type IDs for the selected types
          const { data: typeData } = await supabase
            .from(TABLES.CONTRACT_TYPES)
            .select("id, type")
            .in("type", appliedFilters.types);

          if (typeData && typeData.length > 0) {
            typeIds = typeData.map((t) => t.id);
            query = query.in("contract_type_id", typeIds);
          }
        }

        if (appliedFilters.companies.length > 0) {
          // Get contract type IDs for the selected companies
          const { data: companyData } = await supabase
            .from(TABLES.CONTRACT_TYPES)
            .select("id, company")
            .in("company", appliedFilters.companies);

          if (companyData && companyData.length > 0) {
            companyIds = companyData.map((c) => c.id);
            query = query.in("contract_type_id", companyIds);
          }
        }

        if (
          appliedFilters.dateRange.startDate &&
          appliedFilters.dateRange.endDate
        ) {
          const startDate = new Date(appliedFilters.dateRange.startDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(appliedFilters.dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);

          query = query
            .gte("created_at", startDate.toISOString())
            .lte("created_at", endDate.toISOString());
        }

        // If search term is applied, we need to handle it with a more complex query
        if (appliedFilters.searchTerm) {
          const searchTerm = appliedFilters.searchTerm.toLowerCase();

          // We need to query customers that match the search term
          const { data: matchingCustomers } = await supabase
            .from(TABLES.CUSTOMERS)
            .select("id")
            .or(
              `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
            );

          // Also query matching addresses
          const { data: matchingAddresses } = await supabase
            .from(TABLES.ADDRESSES)
            .select("id")
            .or(
              `street.ilike.%${searchTerm}%,house_number.ilike.%${searchTerm}%,zip_code.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`
            );

          const tempCustomerIds: number[] = [];

          if (matchingCustomers && matchingCustomers.length > 0) {
            matchingCustomers.forEach((c) => tempCustomerIds.push(c.id));
          }

          if (matchingAddresses && matchingAddresses.length > 0) {
            // We need to find customers with these address IDs
            const { data: customersWithAddresses } = await supabase
              .from(TABLES.CUSTOMERS)
              .select("id")
              .in(
                "address_id",
                matchingAddresses.map((a) => a.id)
              );

            if (customersWithAddresses && customersWithAddresses.length > 0) {
              customersWithAddresses.forEach((c) => tempCustomerIds.push(c.id));
            }
          }

          // Remove duplicates
          customerIdsForSearch = [...new Set(tempCustomerIds)];

          if (customerIdsForSearch.length > 0) {
            query = query.in("customer_id", customerIdsForSearch);
          }
        }

        // Get total count with filters applied
        let totalCount = 0;
        try {
          // Need to build a separate query for count
          const countQuery = supabase
            .from(TABLES.CONTRACTS)
            .select("id", { count: "exact" });

          // Apply team scoping to count query
          if (teamWorkerIds !== null && teamWorkerIds.length > 0) {
            countQuery.in("worker_id", teamWorkerIds);
          }

          // Apply the same filters to count query
          if (workerIds.length > 0) {
            countQuery.in("worker_id", workerIds);
          }

          if (typeIds.length > 0) {
            countQuery.in("contract_type_id", typeIds);
          }

          if (companyIds.length > 0) {
            countQuery.in("contract_type_id", companyIds);
          }

          if (
            appliedFilters.dateRange.startDate &&
            appliedFilters.dateRange.endDate
          ) {
            const startDate = new Date(appliedFilters.dateRange.startDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(appliedFilters.dateRange.endDate);
            endDate.setHours(23, 59, 59, 999);

            countQuery
              .gte("created_at", startDate.toISOString())
              .lte("created_at", endDate.toISOString());
          }

          // If search term filters were applied using IDs
          if (customerIdsForSearch.length > 0) {
            countQuery.in("customer_id", customerIdsForSearch);
          }

          const { count } = await countQuery;
          totalCount = count || 0;
        } catch (countErr) {
          console.error("Error getting count:", countErr);
        }

        setTotalCustomers(totalCount);

        // Apply pagination to filtered data
        const from = (currentPage - 1) * pageSize;
        const to = from + pageSize - 1;

        // Execute the query with pagination
        const { data, error } = await query
          .order("created_at", { ascending: false })
          .range(from, to);

        if (error) throw error;

        // Transform and group by customer
        const customerMap = new Map<number, Customer>();

        (data || []).forEach((contract: any) => {
          if (!contract?.customer || !contract?.customer?.address) return;

          const customerId = contract.customer.id;

          if (!customerMap.has(customerId)) {
            // Create new customer entry
            customerMap.set(customerId, {
              id: contract.customer.id,
              first_name: contract.customer.first_name || "",
              last_name: contract.customer.last_name || "",
              email: contract.customer.email || "",
              phone_number: contract.customer.phone_number || "",
              created_at:
                contract.customer.created_at || new Date().toISOString(),
              address: {
                street: contract.customer.address.street || "",
                house_number: contract.customer.address.house_number || "",
                zip_code: contract.customer.address.zip_code || "",
                city: contract.customer.address.city || "",
              },
              contracts: [],
            });
          }

          // Add contract to customer's contracts array
          const customer = customerMap.get(customerId)!;
          customer.contracts.push({
            id: contract.id,
            secure_token: contract.secure_token || "",
            contract_number: contract.contract_number || "",
            contract_type: {
              type: contract.contract_type?.type || "",
              company: contract.contract_type?.company || "",
            },
            worker: {
              full_name: contract.worker?.full_name || "",
            },
          });
        });

        setCustomers(Array.from(customerMap.values()));
      } catch (err) {
        console.error("Error fetching customers:", err);
        setError(
          err instanceof Error ? err.message : "Fehler beim Laden der Kunden"
        );
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchCustomers();
    }
  }, [user, authLoading, navigate, currentPage, pageSize, appliedFilters, teamWorkerIds, teamSelectionReady]);

  const handleDeleteContract = async () => {
    if (!selectedContractId) return;

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from(TABLES.CONTRACTS)
        .delete()
        .eq("id", selectedContractId);

      if (error) throw error;

      // Update local state to remove the deleted contract
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) => ({
          ...customer,
          contracts: customer.contracts.filter(
            (c) => c.id !== selectedContractId
          ),
        })).filter(customer => customer.contracts.length > 0) // Remove customer if no contracts left
      );

      setShowDeleteModal(false);
      setSelectedContractId(null);
    } catch (err) {
      console.error("Error deleting contract:", err);
      alert("Fehler beim Löschen des Vertrags");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomerId) return;

    try {
      setIsDeletingCustomer(true);
      const { error } = await supabase
        .from(TABLES.CUSTOMERS)
        .delete()
        .eq("id", selectedCustomerId);

      if (error) throw error;

      // Update local state to remove the deleted customer
      setCustomers((prevCustomers) =>
        prevCustomers.filter((c) => c.id !== selectedCustomerId)
      );

      setShowDeleteCustomerModal(false);
      setSelectedCustomerId(null);
    } catch (err) {
      console.error("Error deleting customer:", err);
      alert("Fehler beim Löschen des Kunden");
    } finally {
      setIsDeletingCustomer(false);
    }
  };

  if (loading || authLoading) {
    return (
      <NavbarSidebarLayout pageTitle="Alle Kunden">
        <div className="flex h-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (error) {
    return (
      <NavbarSidebarLayout pageTitle="Alle Kunden">
        <div className="p-4">
          <div className="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-gray-800 dark:text-red-400">
            <p>Fehler beim Laden der Kunden: {error}</p>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout pageTitle="Alle Kunden">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem href="/customers">Kunden</BreadcrumbItem>
                <BreadcrumbItem>Alle</BreadcrumbItem>
              </Breadcrumb>
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  Alle Kunden
                </h1>
                <TeamSelector
                  onSelectionChange={handleTeamSelectionChange}
                  currentSelection={teamSelection}
                  workerId={currentWorker?.id ?? null}
                  hideIndividual
                  className="w-auto"
                />
              </div>
            </div>
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="flex w-full items-center gap-2">
                <div className="flex-1">
                  <TextInput
                    icon={HiSearch}
                    placeholder="Nach Namen oder Adresse suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applySearch();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={applySearch}
                  className="inline-flex items-center justify-center rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                >
                  Suchen
                </button>
              </div>
              {/* Filter Dropdowns */}
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-full sm:w-auto">
                  <MultiSelect
                    id="contract-type-select"
                    label="Vertragstyp"
                    options={uniqueValues.types}
                    value={selectedTypes}
                    onChange={setSelectedTypes}
                    placeholder="Vertragstyp auswählen"
                    searchPlaceholder="Nach Vertragstyp suchen..."
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <MultiSelect
                    id="company-select"
                    label="Unternehmen"
                    options={uniqueValues.companies}
                    value={selectedCompanies}
                    onChange={setSelectedCompanies}
                    placeholder="Unternehmen auswählen"
                    searchPlaceholder="Nach Unternehmen suchen..."
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <MultiSelect
                    id="worker-select"
                    label="Berater"
                    options={workerFilterOptions}
                    value={selectedWorkers}
                    onChange={setSelectedWorkers}
                    placeholder="Berater auswählen"
                    searchPlaceholder="Nach Berater suchen..."
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <DateRangePicker
                    label="Datum"
                    onChange={setDateRange}
                    className="w-full sm:w-auto"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={applyFilters}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                  >
                    Filter anwenden
                  </button>
                  <button
                    onClick={resetFilters}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                  >
                    Filter zurücksetzen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow">
                <Table
                  striped
                  className="dark:bg-gray-800 dark:text-gray-400"
                  theme={{
                    root: {
                      shadow: "hidden",
                      wrapper: "relative overflow-x-auto shadow-md sm:rounded-lg",
                    },
                  }}
                >
                  <TableHead>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Kontakt</TableHeadCell>
                    <TableHeadCell>Adresse</TableHeadCell>
                    <TableHeadCell>Vertrag</TableHeadCell>
                    <TableHeadCell>Berater</TableHeadCell>
                    <TableHeadCell>Datum</TableHeadCell>
                    <TableHeadCell>Aktionen</TableHeadCell>
                  </TableHead>
                  <TableBody className="divide-y">
                    {filteredCustomers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        className="bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      >
                        <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {customer.first_name} {customer.last_name}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-gray-900 dark:text-white">{customer.email}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.phone_number}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-gray-900 dark:text-white">
                              {customer.address.street}{" "}
                              {customer.address.house_number}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.address.zip_code}{" "}
                              {customer.address.city}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {customer.contracts.map((contract, index) => (
                            <div key={contract.contract_number}>
                              {index > 0 && (
                                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                              )}
                              <p>{contract.contract_type.type}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {contract.contract_type.company}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {contract.contract_number}
                              </p>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {customer.contracts.map((contract, index) => (
                            <div key={contract.contract_number}>
                              {index > 0 && (
                                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                              )}
                              <p className="text-sm text-gray-900 dark:text-white">
                                {contract.worker.full_name}
                              </p>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {new Date(customer.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex w-fit flex-col gap-2">
                            {customer.contracts.map((contract) => (
                              <div key={contract.id} className="flex gap-2">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/customers/information/${contract.secure_token}`
                                    )
                                  }
                                  className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700"
                                >
                                  Details anzeigen
                                </button>
                                {isAdminOrDirection && (
                                  <button
                                    onClick={() => {
                                      setSelectedContractId(contract.id);
                                      setShowDeleteModal(true);
                                    }}
                                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 dark:bg-red-600 dark:hover:bg-red-700"
                                  >
                                    Löschen
                                  </button>
                                )}
                              </div>
                            ))}
                            {isAdminOrDirection && (
                              <>
                                <hr className="my-1 border-gray-200 dark:border-gray-700" />
                                <button
                                  onClick={() => {
                                    setSelectedCustomerId(customer.id);
                                    setShowDeleteCustomerModal(true);
                                  }}
                                  className="w-full rounded-lg bg-red-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-900 dark:bg-red-800 dark:hover:bg-red-900"
                                >
                                  Kunden löschen
                                </button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              Vorherige
            </button>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={currentPage * pageSize >= totalCustomers}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              Nächste
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Zeige{" "}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * pageSize + 1, totalCustomers)}
                </span>{" "}
                bis{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalCustomers)}
                </span>{" "}
                von <span className="font-medium">{totalCustomers}</span>{" "}
                Ergebnissen
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md p-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:ring-gray-600 dark:hover:bg-gray-600"
                >
                  <span className="sr-only">Vorherige</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage * pageSize >= totalCustomers}
                  className="relative inline-flex items-center rounded-r-md p-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:ring-gray-600 dark:hover:bg-gray-600"
                >
                  <span className="sr-only">Nächste</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showDeleteModal}
        size="md"
        onClose={() => setShowDeleteModal(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Sind Sie sicher, dass Sie diesen Vertrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={handleDeleteContract}
                disabled={isDeleting}
              >
                {isDeleting ? "Löschen..." : "Ja, löschen"}
              </Button>
              <Button
                color="gray"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
      <Modal
        show={showDeleteCustomerModal}
        size="md"
        onClose={() => setShowDeleteCustomerModal(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Sind Sie sicher, dass Sie diesen Kunden und ALLE zugehörigen Verträge löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={handleDeleteCustomer}
                disabled={isDeletingCustomer}
              >
                {isDeletingCustomer ? "Löschen..." : "Ja, Kunden löschen"}
              </Button>
              <Button
                color="gray"
                onClick={() => setShowDeleteCustomerModal(false)}
                disabled={isDeletingCustomer}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </NavbarSidebarLayout>
  );
};

export default AllCustomersPage;
